<template>
  <nav
    :country="entity"
    :hide="matchEntities([]) && !isStaging"
    :class="{ scrolled: !view.atTopOfPage || view.dark }"
    style="display: var(--nav-display, flex);"
    class="SiteNav fixed w-full flex md:justify-between items-center content-end fade"
  >
    <div
      class="LogoWrapper flex justify-between pl-8 pr-8 py-4 xl:pl-0 fade"
      :class="{ 'scrolled-mini': !view.atTopOfPage || isOpen || view.dark }"
    >
      <div
        class="text-qwellow text-16px  xl:ml-140px xl:w-200px md:mt-1 2xl:ml-140px 3xl:ml-200px 4xl:ml-300px"
      >
        <a v-bind:href="'/' + locale"
          ><img src="~@/assets/qwello-logo.svg" class="h-30px md:h-8 lg:h-10"
        /></a>
      </div>
      <div class="md:hidden BtnWrapper mt-1">
        <MobileNavContact />
        <button
          v-on:click="isOpen = !isOpen"
          class="burger-button"
          type="button"
        >
          <img :src="isOpen ? hamburgerImage : crossImage" />
        </button>
      </div>
    </div>
    <div
      :class="isOpen ? 'block m-fadeIn' : 'hidden m-fadeOut'"
      class="mobileNav  md:flex-row md:flex h-screen w-2/3 md:h-auto mr-0 float-right ease-in-out"
    >
      <nav
        class="pt-20 px-6 md:px-0 bg-qwray md:bg-transparent flex-col flex md:flex-row md:ml-2 md:text-18px md:p-0 text-white md:w-full text-16px leading-24px h-full md:h-auto md:justify-end ease-in-out"
      >
        <div class="PageLinks" :data-lang="locale">
          <div
            v-for="(link, index) in routerLinks"
            v-bind:key="index"
            :class="checkForHighlightedLinks(link.highlight)"
          >
            <LinkDropDownContainer
              title="navigation.charging"
              v-if="link.ref.endsWith('experience')"
            >
              <DropDownLink>
                <a
                  class="ExternalLink"
                  :href="`https://qwello.eu/${defaultLang}?skipPopup=true`"
                  >{{ $t('navigation.qwello_eu') }}</a
                >
              </DropDownLink>
              <DropDownLink>
                <router-link
                  class="ExternalLink"
                  :to="`/${locale}/experience`"
                  >{{ $t('navigation.experience') }}</router-link
                >
              </DropDownLink>
            </LinkDropDownContainer>
            <a
              v-else-if="link.text === 'navigation.google_form_cta'"
              v-bind:class="index == 0 ? '' : 'md:ml-4 lg:ml-6'"
            >
              <a :href="link.ref" class="nav-contact">
                {{ $t(link.text) }}
              </a>
            </a>
            <a v-else-if="link.isExternal">
              <a
                class="block"
                v-bind:class="index == 0 ? '' : 'md:ml-4 lg:ml-6'"
                :href="link.ref"
                target="_blank"
                >{{ $t(link.text) }}</a
              >
            </a>
            <router-link
              v-else
              class="block"
              :to="{ path: link.ref }"
              v-bind:class="index == 0 ? '' : 'md:ml-4 lg:ml-6'"
            >
              <div
                v-if="checkForHighlightedLinks(link.highlight).length"
                class="nav-contact"
              >
                {{ $t(link.text) }}
              </div>
              <span v-else>{{ $t(link.text) }}</span>
            </router-link>
          </div>
        </div>

        <div
          v-if="showEnglishLanguageOption"
          class="langPicker"
          :data-lang="locale"
        >
          <a
            :href="`/en${currentRoute}`"
            v-bind:class="locale == 'en' ? 'active_locale' : ''"
            >EN</a
          >
          <span class="ml-1">/</span>
          <a
            :href="`/${defaultLang}${currentRoute}`"
            class="ml-1"
            v-bind:class="locale == defaultLang ? 'active_locale' : ''"
            >{{ defaultLang.toUpperCase() }}</a
          >
        </div>
        <div v-else class="missing-language-padding"></div>
        <div
          class="md:hidden flex flex-col items-center flex-grow justify-end pb-12"
        >
          <div class="leading-5 Available mb-4">
            {{ $t('footer.contact-title') }}
          </div>
          <div
            class="PhNumber flex justify-between flex-row border-white border-2 border-solid rounded-37px content-between justify-evenly mb-4"
          >
            <div class="self-center PhSVG">
              <img src="~@/assets/phone-ic.svg" class="text-white" />
            </div>
            <div class="self-center phone-text">
              <a
                :href="
                  `tel:${$t('footer.phone')
                    .split(' ')
                    .filter(Boolean)
                    .join('')}`
                "
                >{{ $t('footer.phone') }}</a
              >
            </div>
          </div>
        </div>
      </nav>
    </div>
  </nav>
</template>

<script>
import i18n from '@/i18n';
import region from '@/../region.json';
import matchEntities from '../shared/entityHelpers';
import MobileNavContact from '../components/Navigation/MobileNavContact.vue';
import { getRouterLinks } from '../data/navRouterLinks';
import isStaging from '../../utils/isStaging';
import LinkDropDownContainer from '../components/shared/LinkDropDownContainer.vue';
import DropDownLink from '../components/shared/DropDownLink.vue';

const crossImage = require('../assets/Menu.svg');
const hamburgerImage = require('../assets/X.svg');

export default {
  components: { MobileNavContact, LinkDropDownContainer, DropDownLink },
  data() {
    return {
      isOpen: false,
      view: {
        atTopOfPage: true,
        dark: false,
      },
      mobile: window.innerWidth < 768,
      locale: i18n.locale,
      hamburgerImage,
      crossImage,
      entity: region.entity,
      defaultLang: region.defaultLang,
      routerLinks: getRouterLinks(this.$i18n),
      isStaging,
      showEnglishLanguageOption: true,
    };
  },
  // a beforeMount call to add a listener to the window
  beforeMount() {
    window.addEventListener('scroll', this.handleScroll);
    if (!this.$route.meta.navLight) {
      this.view.dark = true;
    } else {
      this.view.dark = false;
    }
  },
  computed: {
    // Return current <route> so it can be concatenated with 'en/<route>' or 'de/<route>'
    // This way users will not be returned to the homepage when selecting their preferred language
    currentRoute() {
      const url = this.$route.path;

      // if url is only 'en' or 'de' then return an empty string
      if (url.length <= 4) return '';

      // otherwise remove 'en' or 'de' and return the rest of the url
      return `/${url
        .split('/')
        .slice(2)
        .join('/')}`;
    },
  },
  methods: {
    // the function to call when the user scrolls, added as a method
    handleScroll() {
      // when the user scrolls, check the pageYOffset
      if (window.pageYOffset > 0) {
        // user is scrolled
        if (this.view.atTopOfPage) this.view.atTopOfPage = false;
      } else if (!this.view.atTopOfPage) this.view.atTopOfPage = true;
    },
    checkForHighlightedLinks(highlight) {
      return highlight ? 'nav-contact-parent' : '';
    },
    matchEntities,
    toggleNavBarVisibility() {
      this.isOpen = false;
      // eslint-disable-next-line
      if (!this.$route.meta.navLight) {
        this.view.dark = true;
      } else {
        this.view.dark = false;
      }
    },
    toggleEnglishLanguageOption() {
      const hide =
        this.entity === 'UK' ||
        this.$router.currentRoute.name === 'OpenMarketCities';
      if (hide) {
        this.showEnglishLanguageOption = false;
      } else {
        this.showEnglishLanguageOption = true;
      }
    },
  },
  watch: {
    $route() {
      this.toggleNavBarVisibility();
      this.toggleEnglishLanguageOption();
    },
  },
};
</script>

<style>
nav {
  z-index: 10;
}
.SiteNav {
  grid-row: 1;
}
.LogoWrapper {
  width: 100%;
}
.scrolled-mini {
  @apply bg-qwray;
  box-shadow: 0px 3px 6px #00000029;
}

@screen md {
  .scrolled {
    @apply bg-qwray;
    box-shadow: 0px 3px 6px #00000029;
  }
}

.fade {
  transition: 0.3s;
}

.m-fadeOut {
  transition: visibility 0s linear 300ms, opacity 300ms;
}
.m-fadeIn {
  transition: visibility 0s linear 0s, opacity 300ms;
}

.router-link-exact-active {
  @apply text-qwellow;
}
.active_locale {
  @apply text-qwellow;
}

.PageLinks {
  display: flex;
  flex-direction: row;
  letter-spacing: 0.5px;
}

.PhSVG {
  max-width: 12px;
  max-height: 12px;
}
.phone-text {
  font-size: 11px;
}

nav[hide='true'] .mobileNav {
  display: none;
}
nav[hide='true'] .BtnWrapper {
  display: none;
}

@media (max-width: 767px) {
  .mobileNav {
    z-index: -1;
    position: fixed;
    height: 100vh !important;
    bottom: 0;
    top: 0;
    right: 0;
  }
  .PageLinks {
    justify-self: space-evenly;
    flex-direction: column;
  }
  .PageLinks > div {
    max-width: max-content;
    padding: 0.5em 0;
  }

  .BtnWrapper {
    z-index: 11;
  }
}
.PhNumber {
  padding: 0.4em 1.2em;
}
.Available {
  color: #a1b1b5;
  font-size: 14px;
}

@media (max-width: 319px) {
  .PhNumber {
    width: 165px;
    padding: 0.4em 0.3em;
  }
  .PhSVG {
    max-width: 12px;
    max-height: 12px;
  }
}
.nav-contact {
  border-radius: 26px;
  border: 2px solid #ffdf22;
  padding: 0 1em;
}
.nav-contact-parent {
  display: none;
}
/* 
  NOTE:(greg)
  This is a quick fix and is very ugly. Sorry.
  But it solves the problem of only showing the nav highlight
  if "entity='NETHERLANDS' && mobileDevice".
  This navigation component is in need of a spring cleaning.

*/
nav[country='NETHERLANDS'] .nav-contact-parent {
  display: block;
}

.nav-contact-mobile {
  border-radius: 26px;
  border: 2px solid #ffdf22;
  padding: 0 1em;
  font-size: 16px;
  color: white;
  font-size: 16px;
  display: block;
  font-size: 12px;
  height: min-content;
  align-self: center;
}
.burger-button {
  display: flex;
  justify-content: center;
  align-items: center;
}
.BtnWrapper {
  display: grid;
  column-gap: 1em;
  grid-template-columns: auto minmax(35px, 1fr);
}
@media (min-width: 380px) {
  .nav-contact-mobile {
    font-size: 16px;
  }
  .phone-text {
    font-size: 14px;
  }
  .PhSVG {
    max-width: 14px;
    max-height: 14px;
  }
  .PhNumber {
    width: 210px;
  }
}
@media (min-width: 767px) {
  .scrolled-mini {
    box-shadow: none;
  }
  .LogoWrapper {
    width: max-content;
  }
  .nav-contact-mobile {
    display: none;
  }
  .nav-contact-parent {
    display: block;
  }
}
.mobileNav {
  width: max-content;
}
@media (min-width: 1200px) {
  .PageLinks {
    letter-spacing: 0.5px;
    font-size: 18px;
  }
}

@media (min-width: 768px) {
  /* UI hack for spainish language*/
  .langPicker[data-lang='es'] {
    font-size: 16px;
  }
  .PageLinks[data-lang='es'] {
    font-size: 14px;
  }

  .langPicker {
    font-size: 20px;
    color: #ffffff;
    margin-left: 10px;
    margin-right: 10px;
  }
  .PageLinks {
    font-size: 16px;
    align-items: center;
  }
}
@media (min-width: 1200px) {
  /* UI hack for spainish language*/
  .langPicker[data-lang='es'] {
    font-size: 18px;
  }
  .PageLinks[data-lang='es'] {
    font-size: 18px;
  }
}

.missing-language-padding {
  margin-right: 3rem;
}
.ExternalLink {
  color: #2b4047;
}
.ExternalLink:hover {
  font-weight: bold;
}
</style>
